import { useEffect, useState } from 'react';
import Styles from '../../assets/css/components/company/companyEdit.module.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ProductList from '../../pages/ProductList';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { getCompanyById, updateCompany, getCompanyWarehouseById, updateCompanyWarehouse, warehouseList, getCountries, getStates, getCities, getCompanyReport, checkCompanyStatus } from '../../services/Api';
import { popUpBoxTrigger } from '../../features/message/popUpSlice';
import { useDispatch } from 'react-redux';
import Loader from '../../utils/Loader';
import { AiOutlineLoading } from 'react-icons/ai';
import PopUpMessage from '../../utils/errors/PopUpMessage';

const CompanyEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('company');
    const [warehouseLoading, setWarehouseLoading] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [originalData, setOriginalData] = useState(null);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState({
        countries: false,
        states: false,
        cities: false
    });

    const [companyData, setCompanyData] = useState({
        id: '',
        customer_id: '',
        company_name: '',
        company_uen_number: '',
        email: '',
        contact_number: '',
        address: '',
        country_id: '',
        state_id: '',
        city_id: '',
        main_contact_person_name: '',
        main_contact_person_email: '',
        main_contact_person_phone_number: '',
        remark: '',
        status: '',
        password: null,
        company_warehouses: []
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [reportData, setReportData] = useState({
        sales_analysis: {},
        total_stock_movment: 0
    });
    const [reportLoading, setReportLoading] = useState(false);

    const [editingWarehouse, setEditingWarehouse] = useState(null);
    const [editWarehouseData, setEditWarehouseData] = useState({
        membership_till: '',
        warehouse_id: '',
        min_lot: '25',
        max_lot: ''
    });

    const [isAddingWarehouse, setIsAddingWarehouse] = useState(false);
    const [newWarehouseData, setNewWarehouseData] = useState({
        membership_till: '',
        warehouse_id: '',
        min_lot: '25',
        max_lot: ''
    });

    // Validation rules - SAME PATTERN AS OTHER COMPONENTS
    const validationRules = {
        company_name: {
            required: true,
            message: 'Please enter a company name'
        },
        company_uen_number: {
            required: true,
            message: 'Please enter a company UEN number'
        },
        email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'Please enter a valid email address'
        },
        contact_number: {
            required: true,
            pattern: /^[6-9]\d{9}$/,
            message: 'Please enter a valid 10-digit phone number starting with 6, 7, 8, or 9'
        },
        address: {
            required: true,
            message: 'Please enter a company address'
        },
        country_id: {
            required: true,
            message: 'Please select a country'
        },
        state_id: {
            required: true,
            message: 'Please select a state'
        },
        city_id: {
            required: true,
            message: 'Please select a city'
        },
        main_contact_person_name: {
            required: true,
            message: 'Please enter a contact person name'
        },
        main_contact_person_email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'Please enter a valid contact email address'
        },
        main_contact_person_phone_number: {
            required: true,
            pattern: /^[6-9]\d{9}$/,
            message: 'Please enter a valid 10-digit contact phone number starting with 6, 7, 8, or 9'
        },
        status: {
            required: true,
            message: 'Please select a status'
        }
    };

    const warehouseValidationRules = {
        membership_till: {
            required: true,
            message: 'Please select a membership till date'
        },
        warehouse_id: {
            required: true,
            message: 'Please select a warehouse location'
        },
        // min_lot: {
        //     required: true,
        //     message: 'Please enter a minimum lot number'
        // },
        max_lot: {
            required: true,
            message: 'Please enter a maximum lot number'
        }
    };

    const handleSetPrimary = async (warehouseIndex) => {
        try {
            const updatedWarehouses = companyData.company_warehouses.map((warehouse, index) => ({
                ...warehouse,
                is_primary: index === warehouseIndex ? 1 : 0
            }));

            setCompanyData(prev => ({
                ...prev,
                company_warehouses: updatedWarehouses
            }));

            showPopup('success', 'Success', 'Primary warehouse updated successfully');
        } catch (error) {
            console.error("Error setting primary warehouse:", error);
            showPopup('error', 'Error', 'Failed to set primary warehouse');
        }
    };

    const handleEditWarehouse = (warehouseIndex) => {
        const warehouse = companyData.company_warehouses[warehouseIndex];
        setEditingWarehouse(warehouseIndex);
        setEditWarehouseData({
            membership_till: warehouse.membership_till.split('T')[0], // Format date for input
            warehouse_id: warehouse.warehouse_id.toString(),
            min_lot: '25',
            // min_lot: warehouse.min_lot.toString(),
            max_lot: warehouse.max_lot.toString()
        });
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        // Don't allow editing lot_min (minimum field)
        if (name === 'min_lot') {
            return;
        }

        if (name === 'max_lot') {
            const numericValue = value.replace(/\D/g, '');
            setEditWarehouseData(prev => ({
                ...prev,
                [name]: numericValue
            }));

            // Clear any existing error for this field
            const errorKey = `edit_${name}_${editingWarehouse}`;

            // Real-time validation for max lot
            if (numericValue) {
                const minValue = 25; // Fixed minimum value
                const maxValue = parseInt(numericValue, 10);

                if (isNaN(maxValue)) {
                    setErrors(prev => ({
                        ...prev,
                        [errorKey]: 'Please enter a valid number'
                    }));
                } else if (maxValue < minValue) {
                    setErrors(prev => ({
                        ...prev,
                        [errorKey]: `Maximum lot must be at least ${minValue}`
                    }));
                } else {
                    // Clear error if valid
                    if (errors[errorKey]) {
                        setErrors(prev => ({
                            ...prev,
                            [errorKey]: ''
                        }));
                    }
                }
            } else {
                // If field is empty, clear the error but keep required validation for form submission
                if (errors[errorKey]) {
                    setErrors(prev => ({
                        ...prev,
                        [errorKey]: ''
                    }));
                }
            }
        } else {
            setEditWarehouseData(prev => ({
                ...prev,
                [name]: value
            }));

            // Clear field-specific errors when user starts typing
            const errorKey = `edit_${name}_${editingWarehouse}`;
            if (errors[errorKey]) {
                setErrors(prev => ({
                    ...prev,
                    [errorKey]: ''
                }));
            }
        }
    };

    const handleAddWarehouse = () => {
        setIsAddingWarehouse(true);
        setNewWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });
    };

    const handleNewWarehouseInputChange = (e) => {
        const { name, value } = e.target;

        // Don't allow editing lot_min (minimum field)
        if (name === 'min_lot') {
            return;
        }

        if (name === 'max_lot') {
            const numericValue = value.replace(/\D/g, '');
            setNewWarehouseData(prev => ({
                ...prev,
                [name]: numericValue
            }));

            // Clear any existing error for this field
            const errorKey = `new_${name}`;

            // Real-time validation for max lot
            if (numericValue) {
                const minValue = 25; // Fixed minimum value
                const maxValue = parseInt(numericValue, 10);

                if (isNaN(maxValue)) {
                    setErrors(prev => ({
                        ...prev,
                        [errorKey]: 'Please enter a valid number'
                    }));
                } else if (maxValue < minValue) {
                    setErrors(prev => ({
                        ...prev,
                        [errorKey]: `Maximum lot must be at least ${minValue}`
                    }));
                } else {
                    // Clear error if valid
                    if (errors[errorKey]) {
                        setErrors(prev => ({
                            ...prev,
                            [errorKey]: ''
                        }));
                    }
                }
            } else {
                // If field is empty, clear the error but keep required validation for form submission
                if (errors[errorKey]) {
                    setErrors(prev => ({
                        ...prev,
                        [errorKey]: ''
                    }));
                }
            }
        } else {
            setNewWarehouseData(prev => ({
                ...prev,
                [name]: value
            }));

            // Clear field-specific errors when user starts typing
            const errorKey = `new_${name}`;
            if (errors[errorKey]) {
                setErrors(prev => ({
                    ...prev,
                    [errorKey]: ''
                }));
            }
        }
    };

    const handleAddNewWarehouse = () => {
        setIsAddingWarehouse(true);
        setNewWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });
    };

    // VALIDATE NEW WAREHOUSE FORM - SAME PATTERN
    const validateNewWarehouseForm = () => {
        const validationErrors = {};

        Object.keys(warehouseValidationRules).forEach(field => {
            const rule = warehouseValidationRules[field];
            const value = newWarehouseData[field];

            // Skip min_lot validation as it's fixed
            if (field === 'min_lot') {
                return;
            }

            if (rule.required) {
                if (!value || value.toString().trim() === '') {
                    validationErrors[`new_${field}`] = rule.message;
                }
            }
        });

        // Additional validation for max lot
        if (newWarehouseData.max_lot) {
            const minValue = 25; // Fixed minimum value
            const maxValue = parseInt(newWarehouseData.max_lot, 10);

            if (isNaN(maxValue)) {
                validationErrors.new_max_lot = 'Please enter a valid number';
            } else if (maxValue < minValue) {
                validationErrors.new_max_lot = `Maximum lot must be at least ${minValue}`;
            }
        } else if (warehouseValidationRules.max_lot.required) {
            // If max_lot is required and empty
            validationErrors.new_max_lot = warehouseValidationRules.max_lot.message;
        }

        setErrors(prev => ({
            ...prev,
            ...validationErrors
        }));

        return Object.keys(validationErrors).length === 0;
    };

    const handleSaveNewWarehouse = () => {
        // Validate form before submission
        if (!validateNewWarehouseForm()) {
            return;
        }

        // Check if warehouse already exists for this company
        const warehouseExists = companyData.company_warehouses.some(
            warehouse => warehouse.warehouse_id === parseInt(newWarehouseData.warehouse_id)
        );

        if (warehouseExists) {
            showPopup('error', 'Error', 'This warehouse is already assigned to the company');
            return;
        }

        // Get warehouse name from warehouses array
        const selectedWarehouse = warehouses.find(w => w.id === parseInt(newWarehouseData.warehouse_id));
        const warehouseName = selectedWarehouse ? selectedWarehouse.name : 'Unknown Warehouse';

        // Add the new warehouse to the existing list - id will be null for new entries
        const newWarehouse = {
            id: null, // New warehouse, no ID yet
            company_id: companyData.id,
            warehouse_id: parseInt(newWarehouseData.warehouse_id),
            name: warehouseName,
            membership_till: newWarehouseData.membership_till,
            min_lot: 25,
            max_lot: parseInt(newWarehouseData.max_lot),
            is_primary: companyData.company_warehouses.length === 0 ? 1 : 0
        };

        setCompanyData(prev => ({
            ...prev,
            company_warehouses: [...prev.company_warehouses, newWarehouse]
        }));

        setIsAddingWarehouse(false);
        setNewWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '25',
            max_lot: ''
        });

        setErrors(prev => {
            const newErrors = { ...prev };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`new_`)) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });

        showPopup('success', 'Success', 'New warehouse added successfully. Click Update to save changes.');
    };

    // VALIDATE EDIT WAREHOUSE FORM - SAME PATTERN
    const validateEditWarehouseForm = () => {
        const validationErrors = {};

        Object.keys(warehouseValidationRules).forEach(field => {
            const rule = warehouseValidationRules[field];
            const value = editWarehouseData[field];

            if (field === 'min_lot') {
                return;
            }

            if (rule.required) {
                if (!value || value.toString().trim() === '') {
                    validationErrors[`edit_${field}_${editingWarehouse}`] = rule.message;
                }
            }
        });

        // Additional validation for max lot
        if (editWarehouseData.max_lot) {
            const minValue = 25; // Fixed minimum value
            const maxValue = parseInt(editWarehouseData.max_lot, 10);

            if (isNaN(maxValue)) {
                validationErrors[`edit_max_lot_${editingWarehouse}`] = 'Please enter a valid number';
            } else if (maxValue < minValue) {
                validationErrors[`edit_max_lot_${editingWarehouse}`] = `Maximum lot must be at least ${minValue}`;
            }
        } else if (warehouseValidationRules.max_lot.required) {
            // If max_lot is required and empty
            validationErrors[`edit_max_lot_${editingWarehouse}`] = warehouseValidationRules.max_lot.message;
        }

        setErrors(prev => ({
            ...prev,
            ...validationErrors
        }));

        return Object.keys(validationErrors).length === 0;
    };

    const handleSaveEdit = () => {
        // Validate form before saving
        if (!validateEditWarehouseForm()) {
            return;
        }

        // Get the current warehouse being edited
        const currentWarehouse = companyData.company_warehouses[editingWarehouse];// Debug log

        // Check if warehouse already exists (excluding the current one being edited)
        const warehouseExists = companyData.company_warehouses.some(
            (warehouse, index) =>
                index !== editingWarehouse &&
                warehouse.warehouse_id === parseInt(editWarehouseData.warehouse_id)
        );

        if (warehouseExists) {
            showPopup('error', 'Error', 'This warehouse is already assigned to the company');
            return;
        }

        // Get warehouse name from warehouses array
        const selectedWarehouse = warehouses.find(w => w.id === parseInt(editWarehouseData.warehouse_id));
        const warehouseName = selectedWarehouse ? selectedWarehouse.name : 'Unknown Warehouse';

        // Create updated warehouse object - PRESERVE THE EXISTING ID
        const updatedWarehouse = {
            ...currentWarehouse,
            id: currentWarehouse.id, // CRITICAL: Keep the original ID
            name: warehouseName,
            membership_till: editWarehouseData.membership_till,
            warehouse_id: parseInt(editWarehouseData.warehouse_id),
            min_lot: 25,
            max_lot: parseInt(editWarehouseData.max_lot)
        };

        // Update the specific warehouse in the array
        const updatedWarehouses = [...companyData.company_warehouses];
        updatedWarehouses[editingWarehouse] = updatedWarehouse;

        setCompanyData(prev => ({
            ...prev,
            company_warehouses: updatedWarehouses
        }));

        setEditingWarehouse(null);
        setEditWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '25',
            max_lot: ''
        });

        // Clear edit-specific errors
        setErrors(prev => {
            const newErrors = { ...prev };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`edit_`)) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });

        showPopup('success', 'Success', 'Warehouse details updated successfully. Click Update to save all changes.');
    };

    const handleCancelNewWarehouse = () => {
        setIsAddingWarehouse(false);
        setNewWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });

        setErrors(prev => {
            const newErrors = { ...prev };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`new_`)) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });
    };

    const handleCancelEdit = () => {
        setEditingWarehouse(null);
        setEditWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });

        setErrors(prev => {
            const newErrors = { ...prev };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`edit_`)) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });
    };

    useEffect(() => {
        const fetchCompanyData = async () => {
            setIsLoading(true);
            try {
                await fetchCountries();
                const data = await getCompanyById(id);
                const companyDataResponse = data.data;

                setOriginalData(companyDataResponse);

                setCompanyData({
                    ...companyData,
                    id: data.data.id,
                    customer_id: data.data.customer_id,
                    company_name: data.data.company_name,
                    company_uen_number: data.data.company_uen_number,
                    email: data.data.email,
                    contact_number: data.data.contact_number,
                    address: data.data.address,
                    country_id: data.data.country_id,
                    state_id: data.data.state_id,
                    city_id: data.data.city_id,
                    main_contact_person_name: data.data.main_contact_person_name,
                    main_contact_person_email: data.data.main_contact_person_email,
                    main_contact_person_phone_number: data.data.main_contact_person_phone_number,
                    remark: data.data.remark,
                    status: data.data.status,
                    company_warehouses: []
                });
                fetchCompanyDataWarehouse(data.data.id);

                // If warehouse has country_id, fetch states
                setTimeout(() => {
                    if (data.data.country_id) {
                        // Find country code from countries array
                        const countryObj = countries.find(c => c.id === data.data.country_id);
                        if (countryObj) {
                            // Fetch states using country code
                            fetchStates(countryObj.code || countryObj.country_code).then(() => {
                                if (data.data.state_id) {
                                    // Fetch cities using state ID
                                    setTimeout(() => {
                                        fetchCities(data.data.state_id);
                                    }, 300);
                                }
                            });
                        }
                    }
                }, 500);
            } catch (error) {
                console.error("Error fetching company data:", error);
                showPopup('error', 'Error', 'Failed to fetch company data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchCompanyData();
    }, [id]);

    const fetchCompanyDataWarehouse = async (companyId) => {
        try {
            const warehouseData = await getCompanyWarehouseById(companyId);
            if (warehouseData && warehouseData.data) {
                const warehousesWithIds = warehouseData.data.map(wh => ({
                    ...wh,
                    id: wh.id || null
                }));

                setCompanyData(prevData => ({
                    ...prevData,
                    company_warehouses: warehousesWithIds || []
                }));
            }
        } catch (error) {
            console.error("Error fetching warehouse data:", error);
            showPopup('error', 'Error', 'Failed to fetch warehouse information');
        }
    };

    const fetchCompanyReport = async () => {
        setReportLoading(true);
        try {
            const response = await getCompanyReport(id);
            if (response.status === 200) {
                setReportData(response.data);
            }
        } catch (error) {
            console.error("Error fetching company report:", error);
            showPopup('error', 'Error', 'Failed to fetch company report');
        } finally {
            setReportLoading(false);
        }
    };

    const fetchCountries = async () => {
        setLoadingLocations(prev => ({ ...prev, countries: true }));
        try {
            const response = await getCountries();
            if (response && response.data && Array.isArray(response.data.data)) {
                setCountries(response.data.data);
            } else {
                console.error("Countries data not in expected format:", response);
                setCountries([]);
                showPopup('error', 'Error', 'Failed to parse countries data');
            }
        } catch (error) {
            console.error("Error fetching countries:", error);
            setCountries([]);
            showPopup('error', 'Error', 'Failed to load countries');
        } finally {
            setLoadingLocations(prev => ({ ...prev, countries: false }));
        }
    };

    const fetchStates = async (countryIdOrCode) => {
        if (!countryIdOrCode) {
            setStates([]);
            setCities([]);
            return;
        }

        setLoadingLocations(prev => ({ ...prev, states: true }));
        setStates([]);
        setCities([]);

        try {
            let countryCode = countryIdOrCode;

            // If countryIdOrCode is numeric (country ID), find the country code
            if (!isNaN(countryIdOrCode)) {
                const country = countries.find(c => c.id == countryIdOrCode);
                if (!country) {
                    console.error('Country not found for ID:', countryIdOrCode);
                    setStates([]);
                    return;
                }
                countryCode = country.code || country.country_code;
            }

            const response = await getStates(countryCode);

            if (response && response.data && Array.isArray(response.data.data)) {
                const statesData = response.data.data;

                if (statesData.length === 0) {
                    setStates([{
                        id: 'no-states',
                        name: 'No states available',
                        disabled: true
                    }]);
                } else {
                    setStates(statesData);
                }
            } else {
                console.error("States data not in expected format:", response);
                setStates([{
                    id: 'no-states',
                    name: 'No states available',
                    disabled: true
                }]);
            }
        } catch (error) {
            console.error("Error fetching states:", error);
            setStates([{
                id: 'no-states',
                name: 'No states available',
                disabled: true
            }]);
        } finally {
            setLoadingLocations(prev => ({ ...prev, states: false }));
        }
    };

    const fetchCities = async (stateId) => {
        if (!stateId) {
            setCities([]);
            return;
        }

        // Don't fetch if "no states" option is selected
        if (stateId === 'no-states') {
            setCities([{
                id: 'no-cities',
                name: 'No cities available',
                disabled: true
            }]);
            return;
        }

        setLoadingLocations(prev => ({ ...prev, cities: true }));
        setCities([]);

        try {
            const response = await getCities(stateId);
            if (response && response.data && Array.isArray(response.data.data)) {
                const citiesData = response.data.data;

                // If citiesData is empty, set a special "no cities" option
                if (citiesData.length === 0) {
                    setCities([{
                        id: 'no-cities',
                        name: 'No cities available',
                        disabled: true
                    }]);
                } else {
                    setCities(citiesData);
                }
            } else {
                console.error("Cities data not in expected format:", response);
                setCities([{
                    id: 'no-cities',
                    name: 'No cities available',
                    disabled: true
                }]);
                showPopup('error', 'Error', 'Failed to parse cities data');
            }
        } catch (error) {
            console.error("Error fetching cities:", error);
            setCities([{
                id: 'no-cities',
                name: 'No cities available',
                disabled: true
            }]);
            showPopup('error', 'Error', 'Failed to load cities');
        } finally {
            setLoadingLocations(prev => ({ ...prev, cities: false }));
        }
    };

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    // VALIDATE COMPANY FORM - SAME PATTERN AS OTHER COMPONENTS
    const validateCompanyForm = () => {
        const validationErrors = {};

        Object.keys(validationRules).forEach(field => {
            const rule = validationRules[field];
            const value = companyData[field];

            // Required field validation
            if (rule.required) {
                if (field === 'status') {
                    if (value === null || value === undefined || value === '') {
                        validationErrors[field] = rule.message;
                    }
                } else {
                    if (!value || value.toString().trim() === '') {
                        validationErrors[field] = rule.message;
                    }
                }
            }

            // Pattern validation
            if (rule.pattern && value) {
                if (!rule.pattern.test(value)) {
                    validationErrors[field] = rule.message;
                }
            }
        });

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Special handling for phone numbers and status
        if (name === 'contact_number' || name === 'main_contact_person_phone_number') {
            if (value !== '') {
                const numericValue = value.replace(/\D/g, '');
                const truncatedValue = numericValue.slice(0, 10);

                setCompanyData(prev => ({
                    ...prev,
                    [name]: truncatedValue
                }));
            } else {
                setCompanyData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else if (name === 'status') {
            setCompanyData(prev => ({
                ...prev,
                [name]: value
            }));
        } else if (name === 'country_id') {
            setCompanyData(prev => ({
                ...prev,
                [name]: value,
                state_id: '',
                city_id: ''
            }));

            // Find the selected country to get its code
            const selectedCountry = countries.find(c => c.id == value);
            if (selectedCountry) {
                setTimeout(() => fetchStates(selectedCountry.code || selectedCountry.country_code), 100);
            }
        } else if (name === 'state_id') {
            setCompanyData(prev => ({
                ...prev,
                [name]: value,
                city_id: ''
            }));

            if (value === 'no-states') {
                setCities([{
                    id: 'no-cities',
                    name: 'No cities available',
                    disabled: true
                }]);
            } else {
                fetchCities(value);
            }
        } else {
            setCompanyData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        const selectElement = e.target;
        if (value !== '') {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Real-time validation for specific fields
        if (name === 'email' && value) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(value)) {
                setErrors(prev => ({ ...prev, email: "Please enter a valid email address" }));
            } else {
                setErrors(prev => ({ ...prev, email: "" }));
            }
        }

        if (name === 'main_contact_person_email' && value) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(value)) {
                setErrors(prev => ({ ...prev, main_contact_person_email: "Please enter a valid contact email address" }));
            } else {
                setErrors(prev => ({ ...prev, main_contact_person_email: "" }));
            }
        }

        if ((name === 'contact_number' || name === 'main_contact_person_phone_number') && value) {
            const phonePattern = /^[6-9]\d{9}$/;
            if (!phonePattern.test(value)) {
                setErrors(prev => ({
                    ...prev,
                    [name]: "Please enter a valid 10-digit phone number starting with 6, 7, 8, or 9"
                }));
            } else {
                setErrors(prev => ({ ...prev, [name]: "" }));
            }
        }
    };

    const getCountryIdFromCode = (countryId) => {
        return countryId;
    };
    const handleTabChange = (tab) => {
        setActiveTab(tab);

        // Clear any state when changing tabs manually
        navigate(location.pathname, { replace: true, state: {} });

        if (tab === 'warehouse' && companyData.company_warehouses.length === 0) {
            setWarehouseLoading(true);
            fetchCompanyDataWarehouse(companyData.id).then(() => {
                setWarehouseLoading(false);
            });
        }

        if (tab === 'report') {
            fetchCompanyReport();
        }
    };

    const handleCompanyUpdate = async () => {
        // Validate form before submission - SAME PATTERN
        if (!validateCompanyForm()) {
            showPopup('error', 'Validation Error', 'Please fill all required fields correctly');
            return;
        }

        // Check if "no states" or "no cities" options are selected
        if (companyData.state_id === 'no-states' || companyData.city_id === 'no-cities') {
            showPopup('error', 'Validation Error', 'Selected location does not have states or cities available. Please select a different country.');
            return;
        }

        // Check if status is being changed to inactive
        const isChangingToInactive = originalData?.status === 1 && companyData.status === "0";

        setLoading(true);
        try {
            // If changing to inactive, first check company status
            if (isChangingToInactive) {
                try {
                    await checkCompanyStatus(id, { status: companyData.status });
                } catch (error) {
                    // If check fails with 422, show the specific error message
                    if (error.response?.status === 422) {
                        const errorMessage = error.response?.data?.message || 'This company is associated with other products and cannot be deactivated.';
                        showPopup('error', 'Cannot Deactivate', errorMessage);
                        setLoading(false);
                        return;
                    }
                    // For other errors, re-throw to be handled by the main catch block
                    throw error;
                }
            }

            // Convert country_code back to country_id for API
            const countryId = getCountryIdFromCode(companyData.country_id);

            const updateData = {
                id: companyData.id,
                customer_id: companyData.customer_id,
                company_name: companyData.company_name,
                company_uen_number: companyData.company_uen_number,
                email: companyData.email,
                contact_number: companyData.contact_number,
                address: companyData.address,
                country_id: companyData.country_id,
                state_id: companyData.state_id,
                city_id: companyData.city_id,
                main_contact_person_name: companyData.main_contact_person_name,
                main_contact_person_email: companyData.main_contact_person_email,
                main_contact_person_phone_number: companyData.main_contact_person_phone_number,
                remark: companyData.remark,
                status: companyData.status,
                password: companyData.password || null,
            };

            const response = await updateCompany(updateData);
            if (response.status === 200) {
                showPopup('success', 'Success', 'Company updated successfully');
                navigate("/company");
            }
        } catch (error) {
            console.error("Update error:", error);
            const errorMessage = error.response?.data?.message || 'Failed to update company';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    // VALIDATE WAREHOUSE FORM - SAME PATTERN
    const validateWarehouseForm = () => {
        const validationErrors = {};

        companyData.company_warehouses.forEach((warehouse, index) => {
            Object.keys(warehouseValidationRules).forEach(field => {
                const rule = warehouseValidationRules[field];
                const value = warehouse[field];

                if (field === 'min_lot') {
                    return;
                }

                if (rule.required) {
                    if (!value || value.toString().trim() === '') {
                        validationErrors[`warehouse_${index}_${field}`] = rule.message;
                    }
                }
            });

            // Additional validation for lot numbers
            if (warehouse.max_lot) {
                const minValue = parseInt('25', 10);
                const maxValue = parseInt(warehouse.max_lot, 10);

                if (maxValue < minValue) {
                    validationErrors[`warehouse_${index}_max_lot`] = `Maximum lot must be at least ${minValue}`;
                }
            }
        });

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleWarehouseUpdate = async () => {
        // Validate form before submission
        if (!validateWarehouseForm()) {
            showPopup('error', 'Validation Error', 'Please fill all required warehouse fields correctly');
            return;
        }

        // Check for duplicate warehouses before sending
        const warehouseIds = new Set();
        const hasDuplicates = companyData.company_warehouses.some(warehouse => {
            if (warehouseIds.has(warehouse.warehouse_id)) {
                return true;
            }
            warehouseIds.add(warehouse.warehouse_id);
            return false;
        });

        if (hasDuplicates) {
            showPopup('error', 'Validation Error', 'Duplicate warehouse locations found. Each warehouse location must be unique.');
            return;
        }

        setLoading(true);
        try {
            // Prepare data for API - ensure proper structure
            const allWarehouses = companyData.company_warehouses.map(warehouse => ({
                id: warehouse.id, // This is CRUCIAL - includes existing warehouse ID
                company_id: companyData.id,
                warehouse_id: parseInt(warehouse.warehouse_id),
                membership_till: warehouse.membership_till,
                min_lot: parseInt(warehouse.min_lot) || 25,
                max_lot: parseInt(warehouse.max_lot),
                is_primary: warehouse.is_primary === 1 || warehouse.is_primary === true || warehouse.is_primary === "1"
            }));

            const updateData = {
                company_warehouses: allWarehouses
            };

            const response = await updateCompanyWarehouse(updateData);

            if (response && response.status === 200) {
                showPopup('success', 'Success', 'Warehouse updated successfully');
                // Fetch updated data from backend to refresh IDs
                await fetchCompanyDataWarehouse(companyData.id);
                navigate("/company");
            }
        } catch (error) {
            console.error("Warehouse update error:", error);
            const errorMessage = error.response?.data?.message || 'Failed to update warehouse';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Set active tab based on navigation state
        if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);

            // Clear the state to prevent retaining on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await warehouseList();
                setWarehouses(response.data);
            } catch (error) {
                console.error("Error fetching warehouses:", error);
            }
        };
        fetchWarehouses();
    }, []);

    useEffect(() => {
        if (originalData && countries.length > 0 && companyData.country_id) {
            // Find the country object to get its code
            const countryObj = countries.find(c => c.id === companyData.country_id);
            if (countryObj) {
                // Fetch states based on country code
                fetchStates(countryObj.code || countryObj.country_code).then(() => {
                    // After states are loaded, fetch cities
                    if (companyData.state_id) {
                        setTimeout(() => {
                            fetchCities(companyData.state_id);
                        }, 300);
                    }
                });
            }
        }
    }, [countries, originalData]);

    const handleWarehouseInputChange = (e, index) => {
        const { name, value } = e.target;
        const updatedWarehouses = [...companyData.company_warehouses];

        if (name === 'min_lot' || name === 'max_lot') {
            const numericValue = value.replace(/\D/g, '');
            updatedWarehouses[index] = {
                ...updatedWarehouses[index],
                [name]: numericValue
            };
        } else {
            updatedWarehouses[index] = {
                ...updatedWarehouses[index],
                [name]: value
            };
        }

        setCompanyData(prev => ({
            ...prev,
            company_warehouses: updatedWarehouses
        }));

        if (errors[`warehouse_${index}_${name}`]) {
            setErrors(prev => ({
                ...prev,
                [`warehouse_${index}_${name}`]: ''
            }));
        }
    };

    // Replace the existing testData with dynamic data
    const salesAnalysisData = Object.entries(reportData.sales_analysis).map(([name, value], index) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: parseInt(value),
        color: index === 0 ? "#2D2A83" : "#C2C3F2"
    }));

    const stockData = [
        { name: "Jan", stock: 100 },
        { name: "Feb", stock: 120 },
        { name: "Mar", stock: 90 },
        { name: "Apr", stock: 130 },
        { name: "May", stock: 110 },
        { name: "Jun", stock: 105 },
        { name: "Jul", stock: 125 },
    ];

    const profitLossData = [
        { month: "Jan", shopify: 30, lazada: 25 },
        { month: "Feb", shopify: 45, lazada: 35 },
        { month: "Mar", shopify: 32, lazada: 30 },
        { month: "Apr", shopify: 50, lazada: 40 },
        { month: "May", shopify: 28, lazada: 35 },
        { month: "Jun", shopify: 20, lazada: 10 },
        { month: "Jul", shopify: 45, lazada: 22 },
    ];

    return (
        <div className={`${Styles.company_edit}`}>
            <PopUpMessage />
            <div className={`${Styles.heading}`}>
                <h2>Company Information</h2>
                <button className={`${Styles.add_btn}`} onClick={() => navigate("/company")}>Back</button>
            </div>

            {isLoading ? (
                <div className={`${Styles.company_form} ${Styles.loading_form}`}>
                    <div className={Styles.form_loader_container}>
                        <Loader size="large" color="primary" />
                    </div>
                </div>
            ) : (
                <div className={`${Styles.company_form}`}>
                    <div className={`${Styles.handle_heading}`}>
                        <div className={`${Styles.handle_form}`}>
                            <div className={`${Styles.tab}`}>
                                <h4 onClick={() => handleTabChange('company')} className={`${activeTab === 'company' ? Styles.active : ''}`}>Company details</h4>
                                {activeTab === 'company' && <hr className={`${Styles.tabBorder}`} />}
                            </div>
                            <div className={`${Styles.tab}`}>
                                <h4 onClick={() => handleTabChange('warehouse')} className={`${activeTab === 'warehouse' ? Styles.active : ''}`}>Warehouse details</h4>
                                {activeTab === 'warehouse' && <hr className={`${Styles.tabBorder}`} />}
                            </div>
                            <div className={`${Styles.tab}`}>
                                <h4 onClick={() => handleTabChange('product')} className={`${activeTab === 'product' ? Styles.active : ''}`}>Product in Stock</h4>
                                {activeTab === 'product' && <hr className={`${Styles.tabBorder}`} />}
                            </div>
                            <div className={`${Styles.tab}`}>
                                <h4 onClick={() => handleTabChange('report')} className={`${activeTab === 'report' ? Styles.active : ''}`}>Reports</h4>
                                {activeTab === 'report' && <hr className={`${Styles.tabBorder}`} />}
                            </div>
                        </div>
                        <hr />
                    </div>

                    {activeTab === 'company' && (
                        <div className={`${Styles.company_details}`}>
                            <div className={`${Styles.title}`}>
                                <h4>Company details</h4>
                                <hr />
                            </div>

                            <div className={`${Styles.form}`}>
                                <div className={`${Styles.label_input}`}>
                                    <label>Customer ID (auto-generated)</label>
                                    <input type="text" value={companyData.customer_id} disabled />
                                </div>
                                <div className={`${Styles.label_input} ${errors.company_name ? Styles.error : ''}`}>
                                    <label>Company name<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter Company Name" name="company_name" value={companyData.company_name} onChange={handleInputChange} />
                                        {errors.company_name && <span className={`${Styles.errorMessage}`}>{errors.company_name}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.company_uen_number ? Styles.error : ''}`}>
                                    <label>Company UEN number<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter UEN Number" name="company_uen_number" value={companyData.company_uen_number} onChange={handleInputChange} />
                                        {errors.company_uen_number && <span className={`${Styles.errorMessage}`}>{errors.company_uen_number}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.contact_number ? Styles.error : ''}`}>
                                    <label>Phone number<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter Phone number" name="contact_number" value={companyData.contact_number} onChange={handleInputChange} />
                                        {errors.contact_number && <span className={`${Styles.errorMessage}`}>{errors.contact_number}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.email ? Styles.error : ''}`}>
                                    <label>Email<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter Email" name="email" value={companyData.email} onChange={handleInputChange} disabled />
                                        {errors.email && <span className={`${Styles.errorMessage}`}>{errors.email}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.address ? Styles.error : ''}`}>
                                    <label>Address<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter Address" name="address" value={companyData.address} onChange={handleInputChange} />
                                        {errors.address && <span className={`${Styles.errorMessage}`}>{errors.address}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.status ? Styles.error : ''}`}>
                                    <label>Status<span>*</span></label>
                                    <div className={`${Styles.select_status}`}>
                                        <select className={`${Styles.company_status}`} name="status" value={companyData.status} onChange={handleInputChange}>
                                            <option value="" hidden>Select Status</option>
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                        {errors.status && <span className={`${Styles.errorMessage}`}>{errors.status}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input}`}>
                                    <label>Country /State /City<span>*</span></label>
                                    <div className={`${Styles.select_container}`}>
                                        <select className={`${Styles.country} ${errors.country_id ? Styles.error : ''}`} name="country_id" value={companyData.country_id} onChange={handleInputChange} disabled={loadingLocations.countries}>
                                            <option value="" hidden>Select Country</option>
                                            {loadingLocations.countries ? (
                                                <option value="" disabled>Loading countries...</option>
                                            ) : Array.isArray(countries) && countries.length > 0 ? (
                                                countries.map(country => (
                                                    <option key={country.id} value={country.id}>
                                                        {country.name}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="" disabled>No countries available</option>
                                            )}
                                        </select>
                                        {errors.country_id && <span className={`${Styles.errorMessage}`}>{errors.country_id}</span>}

                                        <select className={`${Styles.state} ${errors.state_id ? Styles.error : ''}`} name="state_id" value={companyData.state_id} onChange={handleInputChange} disabled={!companyData.country_id || loadingLocations.states}>
                                            <option value="" hidden>Select State</option>
                                            {loadingLocations.states ? (
                                                <option value="" disabled>Loading states...</option>
                                            ) : Array.isArray(states) && states.length > 0 ? (
                                                states.map(state => (
                                                    <option key={state.id} value={state.id} disabled={state.disabled || false}>
                                                        {state.name}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="" disabled>
                                                    {companyData.country_id ? 'No states available' : 'Select country first'}
                                                </option>
                                            )}
                                        </select>
                                        {errors.state_id && <span className={`${Styles.errorMessage}`}>{errors.state_id}</span>}

                                        <select className={`${Styles.city} ${errors.city_id ? Styles.error : ''}`} name="city_id" value={companyData.city_id} onChange={handleInputChange} disabled={!companyData.state_id || loadingLocations.cities || companyData.state_id === 'no-states'}>
                                            <option value="" hidden>Select City</option>
                                            {loadingLocations.cities ? (
                                                <option value="" disabled>{loadingLocations.cities ? 'Loading cities...' : companyData.state_id === 'no-states' ? 'No cities available' : 'Select City'}</option>
                                            ) : Array.isArray(cities) && cities.length > 0 ? (
                                                cities.map(city => (
                                                    <option key={city.id} value={city.id} disabled={city.disabled || false}>
                                                        {city.name}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="" disabled>
                                                    {companyData.state_id === 'no-states' ? 'No cities available' :
                                                        companyData.state_id ? 'No cities available' : 'Select state first'}
                                                </option>
                                            )}
                                        </select>
                                        {errors.city_id && <span className={`${Styles.errorMessage}`}>{errors.city_id}</span>}
                                    </div>
                                </div>
                            </div>

                            <div className={`${Styles.contact_person}`}>
                                <div className={`${Styles.title2}`}>
                                    <h1>Main contact person</h1>
                                    <hr />
                                </div>
                                <div className={`${Styles.form}`}>
                                    <div className={`${Styles.label_input} ${errors.main_contact_person_name ? Styles.error : ''}`}>
                                        <label>Contact name person<span>*</span></label>
                                        <div className={`${Styles.select_field}`}>
                                            <input type="text" placeholder="Enter Name Person" name="main_contact_person_name" value={companyData.main_contact_person_name} onChange={handleInputChange} />
                                            {errors.main_contact_person_name && <span className={`${Styles.errorMessage}`}>{errors.main_contact_person_name}</span>}
                                        </div>
                                    </div>
                                    <div className={`${Styles.label_input} ${errors.main_contact_person_email ? Styles.error : ''}`}>
                                        <label>Email<span>*</span></label>
                                        <div className={`${Styles.select_field}`}>
                                            <input type="text" placeholder="Enter Email" name="main_contact_person_email" value={companyData.main_contact_person_email} onChange={handleInputChange} />
                                            {errors.main_contact_person_email && <span className={`${Styles.errorMessage}`}>{errors.main_contact_person_email}</span>}
                                        </div>
                                    </div>
                                    <div className={`${Styles.label_input} ${errors.main_contact_person_phone_number ? Styles.error : ''}`}>
                                        <label>Phone number<span>*</span></label>
                                        <div className={`${Styles.select_field}`}>
                                            <input type="text" placeholder="Enter Phone number" name="main_contact_person_phone_number" value={companyData.main_contact_person_phone_number} onChange={handleInputChange} />
                                            {errors.main_contact_person_phone_number && <span className={`${Styles.errorMessage}`}>{errors.main_contact_person_phone_number}</span>}
                                        </div>
                                    </div>
                                    <div className={`${Styles.label_input}`}>
                                        <label>Remark</label>
                                        <div className={`${Styles.select_field}`}>
                                            <input type="text" placeholder="Enter Remark" name="remark" value={companyData.remark} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={`${Styles.buttons}`}>
                                <button onClick={handleCompanyUpdate} disabled={loading}>
                                    {loading ? <AiOutlineLoading className={`${Styles.loading_icon}`} /> : 'Update'}
                                </button>
                                <button onClick={() => navigate("/company")}>Cancel</button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'warehouse' && (
                        <div className={`${Styles.warehouse_details}`}>
                            <div className={`${Styles.title}`}>
                                <h4>Warehouse details</h4>
                                <hr />
                            </div>

                            {/* Customer ID and Company Name (Read-only) */}
                            <div className={`${Styles.form}`}>
                                <div className={`${Styles.label_input}`}>
                                    <label>Customer ID (auto-generated)</label>
                                    <input type="text" value={companyData.customer_id} disabled />
                                </div>
                                <div className={`${Styles.label_input}`}>
                                    <label>Company name</label>
                                    <input type="text" value={companyData.company_name} disabled />
                                </div>
                            </div>

                            {/* Warehouse Details Section */}
                            <div className={`${Styles.warehouse_section}`}>
                                <div className={`${Styles.warehouse_header}`}>
                                    <label>Warehouse details</label>
                                    <span>Select assign warehouse lot</span>
                                </div>

                                <div className={`${Styles.warehouse_lot_selection}`}>
                                    {/* Warehouse Cards */}
                                    <div className={`${Styles.warehouse_cards}`}>
                                        {companyData.company_warehouses.length > 0 ? (
                                            companyData.company_warehouses.map((warehouse, index) => {
                                                const warehouseInfo = warehouses.find(w => w.id === warehouse.warehouse_id);
                                                const warehouseName = warehouse.name || (warehouseInfo ? warehouseInfo.name : `Warehouse ${warehouse.warehouse_id}`);

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`${Styles.warehouse_card} ${warehouse.is_primary ? Styles.primary : ''}`}
                                                    >
                                                        <div className={`${Styles.radio_indicator}`}>
                                                            <div className={`${Styles.radio_circle} ${warehouse.is_primary ? Styles.selected : ''}`}>
                                                                {warehouse.is_primary && <div className={`${Styles.radio_dot}`}></div>}
                                                            </div>
                                                        </div>

                                                        <div className={`${Styles.warehouse_info}`}>
                                                            <h3>{warehouseName}</h3>
                                                            <p>Lot # {warehouse.min_lot}-{warehouse.max_lot}</p>
                                                            <p>Membership till {new Date(warehouse.membership_till).toLocaleDateString()}</p>
                                                        </div>

                                                        <div className={`${Styles.warehouse_actions}`}>
                                                            {!warehouse.is_primary && (
                                                                <button
                                                                    className={`${Styles.set_primary_btn}`}
                                                                    onClick={() => handleSetPrimary(index)}
                                                                >
                                                                    Set as primary
                                                                </button>
                                                            )}
                                                            <button
                                                                className={`${Styles.edit_btn}`}
                                                                onClick={() => handleEditWarehouse(index)}
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className={`${Styles.no_warehouses}`}>
                                                <p>No warehouses associated with this company</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Add New Warehouse Button */}
                                    {!isAddingWarehouse && (
                                        <button
                                            className={`${Styles.add_warehouse_btn}`}
                                            onClick={handleAddNewWarehouse}
                                        >
                                            <span>+</span> Add new warehouse lot
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Add New Warehouse Form (Show only when adding) */}
                            {isAddingWarehouse && (
                                <div className={`${Styles.new_warehouse_form}`}>
                                    <h3>Add New Warehouse</h3>

                                    <div className={`${Styles.form_content}`}>
                                        <div className={`${Styles.form_row}`}>
                                            <div className={`${Styles.label_input} ${errors.new_membership_till ? Styles.error : ''}`}>
                                                <label>Membership till<span>*</span></label>
                                                <input
                                                    type="date"
                                                    name="membership_till"
                                                    value={newWarehouseData.membership_till}
                                                    onChange={handleNewWarehouseInputChange}
                                                    min={new Date().toISOString().split('T')[0]}
                                                    className={errors.new_membership_till ? Styles.errorInput : ''}
                                                />
                                                {errors.new_membership_till &&
                                                    <span className={`${Styles.errorMessage}`}>{errors.new_membership_till}</span>
                                                }
                                            </div>

                                            <div className={`${Styles.label_input} ${errors.new_warehouse_id ? Styles.error : ''}`}>
                                                <label>Warehouse location<span>*</span></label>
                                                <select
                                                    name="warehouse_id"
                                                    value={newWarehouseData.warehouse_id}
                                                    onChange={handleNewWarehouseInputChange}
                                                    className={errors.new_warehouse_id ? Styles.errorInput : ''}
                                                >
                                                    <option value="" hidden>Select Warehouse</option>
                                                    {warehouses
                                                        .filter(w =>
                                                            w.status === 1 &&
                                                            !companyData.company_warehouses.some(cw => cw.warehouse_id === w.id)
                                                        )
                                                        .map(w => (
                                                            <option key={w.id} value={w.id}>
                                                                {w.name}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                                {errors.new_warehouse_id &&
                                                    <span className={`${Styles.errorMessage}`}>{errors.new_warehouse_id}</span>
                                                }
                                            </div>
                                        </div>

                                        <div className={`${Styles.form_row}`}>
                                            <div className={`${Styles.label_input} ${errors.new_max_lot ? Styles.error : ''}`}>
                                                <label>Lot #<span>*</span></label>
                                                <div className={`${Styles.lot_inputs}`}>
                                                    <input
                                                        type="text"
                                                        placeholder="Minimum"
                                                        name="min_lot"
                                                        value="25"
                                                        readOnly
                                                        disabled
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Maximum"
                                                        name="max_lot"
                                                        value={newWarehouseData.max_lot}
                                                        onChange={handleNewWarehouseInputChange}
                                                        className={errors.new_max_lot ? Styles.errorInput : ''}
                                                    />
                                                </div>
                                                {errors.new_max_lot && (
                                                    <div className={`${Styles.errorMessage}`}>
                                                        {errors.new_max_lot}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className={`${Styles.form_buttons}`}>
                                            <button
                                                className={`${Styles.save_btn}`}
                                                onClick={handleSaveNewWarehouse}
                                            >
                                                Add Warehouse
                                            </button>
                                            <button
                                                className={`${Styles.cancel_btn}`}
                                                onClick={handleCancelNewWarehouse}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Edit Form (Show only when editing) */}
                            {editingWarehouse !== null && (
                                <div className={`${Styles.edit_form_overlay}`}>
                                    <div className={`${Styles.edit_form}`}>
                                        <h3>Edit Warehouse Details</h3>

                                        <div className={`${Styles.form_content}`}>
                                            <div className={`${Styles.form_row}`}>
                                                <div className={`${Styles.label_input} ${errors[`edit_membership_till_${editingWarehouse}`] ? Styles.error : ''}`}>
                                                    <label>Membership till<span>*</span></label>
                                                    <input
                                                        type="date"
                                                        name="membership_till"
                                                        value={editWarehouseData.membership_till}
                                                        onChange={handleEditInputChange}
                                                        min={new Date().toISOString().split('T')[0]}
                                                        className={errors[`edit_membership_till_${editingWarehouse}`] ? Styles.errorInput : ''}
                                                    />
                                                    {errors[`edit_membership_till_${editingWarehouse}`] &&
                                                        <span className={`${Styles.errorMessage}`}>{errors[`edit_membership_till_${editingWarehouse}`]}</span>
                                                    }
                                                </div>

                                                <div className={`${Styles.label_input} ${errors[`edit_warehouse_id_${editingWarehouse}`] ? Styles.error : ''}`}>
                                                    <label>Warehouse location<span>*</span></label>
                                                    <select
                                                        name="warehouse_id"
                                                        value={editWarehouseData.warehouse_id}
                                                        onChange={handleEditInputChange}
                                                        className={errors[`edit_warehouse_id_${editingWarehouse}`] ? Styles.errorInput : ''}
                                                    >
                                                        <option value="" hidden>Select Warehouse</option>
                                                        {warehouses.filter(w => w.status === 1).map(w => (
                                                            <option key={w.id} value={w.id}>
                                                                {w.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors[`edit_warehouse_id_${editingWarehouse}`] &&
                                                        <span className={`${Styles.errorMessage}`}>{errors[`edit_warehouse_id_${editingWarehouse}`]}</span>
                                                    }
                                                </div>
                                            </div>

                                            <div className={`${Styles.form_row}`}>
                                                <div className={`${Styles.label_input} ${errors[`edit_max_lot_${editingWarehouse}`] ? Styles.error : ''}`}>
                                                    <label>Lot #<span>*</span></label>
                                                    <div className={`${Styles.lot_inputs}`}>
                                                        <input
                                                            type="text"
                                                            placeholder="Minimum"
                                                            name="min_lot"
                                                            value="25"
                                                            readOnly
                                                            disabled
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="Maximum"
                                                            name="max_lot"
                                                            value={editWarehouseData.max_lot}
                                                            onChange={handleEditInputChange}
                                                            className={errors[`edit_max_lot_${editingWarehouse}`] ? Styles.errorInput : ''}
                                                        />
                                                    </div>
                                                    {errors[`edit_max_lot_${editingWarehouse}`] && (
                                                        <div className={`${Styles.errorMessage}`}>
                                                            {errors[`edit_max_lot_${editingWarehouse}`]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className={`${Styles.edit_form_buttons}`}>
                                                <button
                                                    className={`${Styles.save_btn}`}
                                                    onClick={handleSaveEdit}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className={`${Styles.cancel_btn}`}
                                                    onClick={handleCancelEdit}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Update Button */}
                            <div className={`${Styles.buttons}`}>
                                <button onClick={handleWarehouseUpdate} disabled={loading}>
                                    {loading ? <AiOutlineLoading className={`${Styles.loading_icon}`} /> : 'Update'}
                                </button>
                                <button onClick={() => navigate("/company")}>Cancel</button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'product' && (
                        <div className={`${Styles.product_details}`}>
                            <h2>Product in stock List</h2>
                            {/* <ProductList /> */}
                            {/* <ProductList companyId={id} filterByCompany={true} /> */}
                            <ProductList companyId={id} companyName={companyData.company_name} filterByCompany={true} navigateFrom="companyEdit" activeTab="product" />
                        </div>
                    )}

                    {activeTab === 'report' && (
                        <div className={`${Styles.report_details}`}>
                            <div className={`${Styles.handle_form}`}>
                                <div className={`${Styles.tab}`}>
                                    <h4>Company Reports</h4>
                                    <hr className={`${Styles.tabBorder}`} />
                                </div>
                            </div>
                            <hr />

                            <div className={`${Styles.report_cards}`}>
                                {/* Sales Analysis Card */}
                                <div className={`${Styles.sales_card}`}>
                                    <div className={`${Styles.sales_analysis}`}>
                                        <h1>Sales analysis</h1>
                                        {reportLoading ? (
                                            <p>Loading...</p>
                                        ) : (
                                            <ul>
                                                {salesAnalysisData.map((item, index) => (
                                                    <li key={index} style={{ listStyle: 'none' }}>
                                                        <span style={{ color: item.color }}> ● </span>{item.name} {item.value}%
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className={`${Styles.pie_chart}`}>
                                        <ResponsiveContainer width={200} height={200}>
                                            <PieChart>
                                                <Pie data={salesAnalysisData} dataKey="value" cx="50%" cy="50%" outerRadius={40} innerRadius={25} paddingAngle={5}>
                                                    {salesAnalysisData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* Stock Movement Card */}
                                <div className={`${Styles.stock_card}`}>
                                    <div className={`${Styles.content}`}>
                                        <h1>Total stock movement</h1>
                                        {reportLoading ? (
                                            <p>Loading...</p>
                                        ) : (
                                            <>
                                                <h2>{reportData.total_stock_movment?.toLocaleString() || '0'}</h2>
                                                <p><span style={{ color: "green" }}>40%</span> Vs last month</p>
                                            </>
                                        )}
                                    </div>
                                    <div className={`${Styles.chart}`}>
                                        <ResponsiveContainer width={200} height={100}>
                                            <LineChart data={stockData}>
                                                <XAxis dataKey="name" hide />
                                                <YAxis hide />
                                                <Tooltip />
                                                <Line type="monotone" dataKey="stock" stroke="#3E54AC" strokeWidth={2} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>

                            {/* Profit/Loss Graph */}
                            <div className={`${Styles.report_graph}`}>
                                <h2>Company Sales graph</h2>
                                <div className={`${Styles.graph}`}>
                                    <div className={`${Styles.graph_content}`}>
                                        <h3>Company Sales graph</h3>
                                        <p>Track your product Sales</p>
                                    </div>
                                    <div className={`${Styles.bar_chart}`}>
                                        <ResponsiveContainer width="100%" height={450}>
                                            <BarChart data={profitLossData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="shopify" fill="#A6B1E1" barSize={30} />
                                                <Bar dataKey="lazada" fill="#3E54AC" barSize={30} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CompanyEdit;
